// ==========================================================================
// SISTEMA DE COLOR PARA GRÁFICOS — GestiónEJP
// Fuente única de colores y opciones base para Chart.js (react-chartjs-2).
//
// Reglas (ver design system → "Gráficos"):
//  1. Una sola serie = un solo color (PRIMARIO). Nunca colorear cada barra distinta
//     cuando todas son la misma medida: el largo de la barra ya dice cuánto.
//  2. Varias series / partes de un todo = PALETA_CATEGORICA en orden fijo, sin ciclar.
//     Más de MAX_CATEGORIAS categorías → se agrupan las menores en "Otros" (gris).
//  3. Estados con significado (finalizado, rechazado, vencido) = colores de estado,
//     nunca de la paleta categórica.
//  4. Datos ordenados (etapas, rangos) = rampa de un solo tono (rampaSecuencial).
//
// La paleta categórica fue validada (contraste ≥ 3:1 sobre blanco, separación para
// daltonismo protan/deutan ΔE ≥ 11 entre vecinos). El ORDEN es parte de la validación:
// no reordenar ni agregar colores sin volver a validar.
// ==========================================================================
import {
    Chart as ChartJS, CategoryScale, LinearScale, BarElement, LineElement, PointElement,
    ArcElement, Title, Tooltip, Legend, Filler,
} from 'chart.js';

ChartJS.register(
    CategoryScale, LinearScale, BarElement, LineElement, PointElement, ArcElement,
    Title, Tooltip, Legend, Filler
);

export const FUENTE_GRAFICOS = "'Public Sans', -apple-system, 'Segoe UI', Helvetica, Arial, sans-serif";

/** Color de una serie única (barras de una sola medida, línea de evolución). */
export const PRIMARIO = '#2563eb';

/** Paleta categórica validada, en orden fijo: azul, ámbar, teal, violeta, rosa, celeste, verde. */
export const PALETA_CATEGORICA = [
    '#2563eb', // 1 azul
    '#d97706', // 2 ámbar
    '#0d9488', // 3 teal
    '#7c3aed', // 4 violeta
    '#db2777', // 5 rosa
    '#0284c7', // 6 celeste
    '#65a30d', // 7 verde
];

/** Color de la porción "Otros" y de categorías sin dato. */
export const COLOR_OTROS = '#94a3b8';

/** Máximo de porciones/series con color propio antes de agrupar en "Otros". */
export const MAX_CATEGORIAS = 6;

/** Colores de estado: SOLO para estados con significado, siempre acompañados de etiqueta. */
export const COLOR_ESTADO = {
    exito: '#15803d',
    advertencia: '#d97706',
    peligro: '#dc2626',
    info: '#0284c7',
    neutro: '#94a3b8',
};

/** Rampa de un solo tono (azul) para datos ordenados: claro → oscuro. */
const RAMPA_AZUL = ['#60a5fa', '#3b82f6', '#2563eb', '#1d4ed8', '#1e40af', '#1e3a8a'];

/** Tinta de textos y grilla (tokens del sistema). */
export const TINTA = {
    texto: '#334155',
    textoSecundario: '#64748b',
    grilla: '#e2e8f0',
    tooltip: '#0f172a',
    superficie: '#ffffff',
};

/** Color de la serie i (0-based). Más allá de la paleta devuelve gris: agrupar en "Otros". */
export function colorSerie(i) {
    return PALETA_CATEGORICA[i] || COLOR_OTROS;
}

/** n colores categóricos en orden; los que excedan la paleta quedan en gris. */
export function coloresCategoricos(n) {
    return Array.from({ length: n }, (_, i) => colorSerie(i));
}

/** n tonos de la rampa azul (para etapas / rangos ordenados). */
export function rampaSecuencial(n) {
    if (n <= 1) return [PRIMARIO];
    return Array.from({ length: n }, (_, i) => {
        const idx = Math.round((i * (RAMPA_AZUL.length - 1)) / (n - 1));
        return RAMPA_AZUL[idx];
    });
}

/** Color con transparencia (para el relleno bajo una línea). */
export function conAlpha(hex, alpha) {
    const h = hex.replace('#', '');
    const r = parseInt(h.slice(0, 2), 16);
    const g = parseInt(h.slice(2, 4), 16);
    const b = parseInt(h.slice(4, 6), 16);
    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

/**
 * Agrupa las categorías menores en "Otros" para que una torta/dona no tenga más de
 * `max` porciones con color propio. Ordena de mayor a menor.
 * @returns {{labels: string[], data: number[], colores: string[]}}
 */
export function agruparEnOtros(labels, data, { max = MAX_CATEGORIAS, colorPara } = {}) {
    const pares = labels.map((l, i) => ({ l, v: Number(data[i]) || 0 }))
        .sort((a, b) => b.v - a.v);
    const principales = pares.length > max ? pares.slice(0, max - 1) : pares;
    const resto = pares.length > max ? pares.slice(max - 1) : [];
    const salidaLabels = principales.map((p) => p.l);
    const salidaData = principales.map((p) => p.v);
    const colores = principales.map((p, i) => (colorPara ? colorPara(p.l, i) : colorSerie(i)));
    if (resto.length) {
        salidaLabels.push(`Otros (${resto.length})`);
        salidaData.push(resto.reduce((s, p) => s + p.v, 0));
        colores.push(COLOR_OTROS);
    }
    return { labels: salidaLabels, data: salidaData, colores };
}

/** Opciones base compartidas por todos los gráficos de la app. Hacer spread y pisar lo propio. */
export const OPCIONES_BASE = {
    responsive: true,
    maintainAspectRatio: true,
    layout: { padding: { top: 8, bottom: 8, left: 8, right: 8 } },
    plugins: {
        legend: {
            display: true,
            position: 'bottom',
            labels: {
                font: { family: FUENTE_GRAFICOS, size: 12, weight: '500' },
                color: TINTA.texto,
                padding: 16,
                usePointStyle: true,
                pointStyle: 'circle',
                boxWidth: 8,
                boxHeight: 8,
            },
        },
        tooltip: {
            enabled: true,
            backgroundColor: TINTA.tooltip,
            titleFont: { family: FUENTE_GRAFICOS, size: 13, weight: '600' },
            bodyFont: { family: FUENTE_GRAFICOS, size: 12 },
            padding: 12,
            cornerRadius: 8,
            boxPadding: 4,
        },
    },
    elements: {
        arc: { borderWidth: 2, borderColor: TINTA.superficie, hoverOffset: 6 },
        bar: { borderRadius: 4, borderSkipped: 'start' },
        line: { tension: 0.3, borderWidth: 2 },
        point: { radius: 3, hoverRadius: 5, borderWidth: 2, backgroundColor: TINTA.superficie },
    },
};

/** Ejes estándar: grilla horizontal suave, sin grilla vertical, sin borde. */
export const EJES_BASE = {
    x: {
        grid: { display: false },
        border: { display: false },
        ticks: { font: { family: FUENTE_GRAFICOS, size: 11 }, color: TINTA.textoSecundario },
    },
    y: {
        beginAtZero: true,
        grid: { color: TINTA.grilla, drawTicks: false },
        border: { display: false },
        ticks: { font: { family: FUENTE_GRAFICOS, size: 11 }, color: TINTA.textoSecundario, padding: 8 },
    },
};

/** Dataset de barras de UNA sola medida (todas las barras del mismo color). */
export function datasetBarrasSimple(label, data, extra = {}) {
    return {
        label,
        data,
        backgroundColor: PRIMARIO,
        hoverBackgroundColor: '#1d4ed8',
        borderRadius: 4,
        maxBarThickness: 40,
        ...extra,
    };
}

/** Dataset de línea para la serie i (relleno suave opcional solo para una serie). */
export function datasetLinea(label, data, i = 0, { relleno = false, ...extra } = {}) {
    const color = colorSerie(i);
    return {
        label,
        data,
        borderColor: color,
        backgroundColor: relleno ? conAlpha(color, 0.08) : color,
        pointBackgroundColor: TINTA.superficie,
        pointBorderColor: color,
        fill: relleno,
        ...extra,
    };
}

// Defaults globales: cualquier gráfico que no pase opciones propias igual sale con la
// tipografía y tintas del sistema.
ChartJS.defaults.font.family = FUENTE_GRAFICOS;
ChartJS.defaults.color = TINTA.textoSecundario;
ChartJS.defaults.borderColor = TINTA.grilla;
ChartJS.defaults.plugins.tooltip.backgroundColor = TINTA.tooltip;
ChartJS.defaults.plugins.legend.labels.usePointStyle = true;
